import { useId } from 'react';

import { BOOK_PATH, B_PATH, R_PATH } from './logoPaths';

/**
 * The "RB" book logomark as a single, fully-scalable SVG.
 *
 * - `cutout` (default): the R and B are knocked out of the book as transparent
 *   holes (via an SVG <mask>), so whatever is behind the logo shows through —
 *   the same look as `trans_logo.png`.
 * - `filled`: the R and B are drawn on top of the book in their own color.
 *
 * Colors are driven by `currentColor`, so any Tailwind `text-*` class (including
 * ones backed by your generated CSS vars) works:
 *   - `bookClassName`   sets the book color
 *   - `letterClassName` sets the letter color (filled variant only)
 */
export const LogoMark = (props: LogoMarkProps) => {
  const { variant = 'cutout', bookClassName, letterClassName, className, ...rest } = props;
  const maskId = useId();

  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" {...rest}>
      {variant === 'cutout' ? (
        <>
          <mask id={maskId}>
            {/* white = keep (book), black = cut out (letters) */}
            <path d={BOOK_PATH} fill="white" />
            <path d={R_PATH} fill="black" />
            <path d={B_PATH} fill="black" />
          </mask>
          <path d={BOOK_PATH} fill="currentColor" mask={`url(#${maskId})`} className={bookClassName} />
        </>
      ) : (
        <>
          <path d={BOOK_PATH} fill="currentColor" className={bookClassName} />
          <path d={R_PATH} fill="currentColor" className={letterClassName} />
          <path d={B_PATH} fill="currentColor" className={letterClassName} />
        </>
      )}
    </svg>
  );
};

type LogoMarkProps = Omit<React.SVGProps<SVGSVGElement>, 'fill'> & {
  variant?: 'cutout' | 'filled';
  bookClassName?: string;
  letterClassName?: string;
};
